import { BulletinIncidentTime, IncidentTimeType } from "./types";

/**
 * Applies a time-type change to a row, dropping whatever the new type cannot express.
 *
 * A separate function rather than a branch inside the row component because these are the rules
 * phase 3's schema has to agree with, and they are far easier to pin down here than through a date
 * picker's DOM.
 */
export const applyTimeType = (
  row: BulletinIncidentTime,
  timeType: IncidentTimeType,
): BulletinIncidentTime => {
  switch (timeType) {
    case "unknown":
    case "not_applicable":
      // Neither type carries a date at all.
      return { ...row, timeType, startDate: null, endDate: null };
    case "time_unknown":
      // Date only. The time component was entered under a type that asked for one, so it is truncated
      // rather than kept as a value the officer never confirmed — the old flow does the same.
      return {
        ...row,
        timeType,
        startDate: row.startDate?.startOf("day") ?? null,
        endDate: null,
      };
    case "exact":
      // Only `range` has an end date. The old flow leaves a stale `end_date` behind here; it is
      // dropped instead, since `BulletinIncidentTime.endDate` documents itself as range-only and a
      // value no input shows can still reach the payload.
      return { ...row, timeType, endDate: null };
    case "range":
      return { ...row, timeType };
  }
};

export type IncidentTimeErrorCode =
  | "start_required"
  | "end_required"
  | "end_before_start";

export type IncidentTimeErrors = Partial<
  Record<"startDate" | "endDate", IncidentTimeErrorCode>
>;

/**
 * Which dates the chosen type actually requires.
 *
 * `generateValidationCriteria` reports `incidentTimes: true` unconditionally — a rule that says
 * nothing — so this is the prototype's, and it is the same shape as `applyTimeType` above: the type
 * decides which dates exist, and every date the type shows is required. `unknown` and
 * `not_applicable` show none, so a row of either kind is always complete.
 */
export const validateIncidentTime = (
  row: BulletinIncidentTime,
): IncidentTimeErrors => {
  const errors: IncidentTimeErrors = {};

  if (row.timeType !== "unknown" && row.timeType !== "not_applicable") {
    if (!row.startDate) errors.startDate = "start_required";
    if (row.timeType === "range") {
      if (!row.endDate) {
        errors.endDate = "end_required";
      } else if (row.startDate && row.endDate.isBefore(row.startDate)) {
        // Both dates present but the wrong way round. The pickers make this awkward to do by
        // hand, but a loaded bulletin or a report hand-off can carry one, and "present" was the
        // only thing checked — so an inverted range finalized without complaint.
        errors.endDate = "end_before_start";
      }
    }
  }

  return errors;
};
